import _cloneDeep from 'lodash/cloneDeep';
import _findIndex from 'lodash/findIndex';
import _keyBy from 'lodash/keyBy';
import _sortBy from 'lodash/sortBy';
import React, { Component } from 'react';
import { Button, Tree } from 'antd';
import { Query, Mutation } from '@apollo/react-components';
import { gql } from '@apollo/client';
import routes from '@achievable-me/tundra-wizard/routes';
import { reverse } from 'named-urls';
import { PropsRoute } from '$components/Routes';
import SpinnerError from '$components/library/SpinnerError';
import ProductContentEdit from '$components/staff/ProductContentEdit';
import { inflateContent } from '@achievable-me/tundra-wizard/contentHelpers';
import styles from './styles.scss';

const STAFF_PRODUCT_CONTENT = gql`
  query StaffProductContent($input: ProductInput!) {
    product(input: $input) {
      product {
        uuid
        slug
        content {
          uuid
          slug
          availability
          parentUuid
          position
          name
          markdown
        }
      }
    }
  }
`;

const STAFF_ADD_CONTENT = gql`
  mutation StaffAddContent($input: AddContentInput!) {
    addContent(input: $input) {
      content {
        uuid
        slug
        parentUuid
        position
        name
        markdown
      }
    }
  }
`;

const STAFF_DELETE_CONTENT = gql`
  mutation StaffDeleteContent($input: DeleteContentInput!) {
    deleteContent(input: $input) {
      content {
        uuid
      }
    }
  }
`;

const STAFF_REORDER_CONTENT = gql`
  mutation StaffReorderContent($input: ReorderContentInput!) {
    reorderContent(input: $input) {
      content {
        uuid
        parentUuid
        position
      }
    }
  }
`;

class ProductContent extends Component {
  handleAddNode = ({ addContent, product }) => {
    const { content } = product;

    const nodesAtLevel = content.filter((c) => c.parentUuid === null);
    const sortedNodesAtLevel = _sortBy(nodesAtLevel, 'position');

    let position;
    if (sortedNodesAtLevel.length === 0) {
      position = 1000;
    } else {
      position = sortedNodesAtLevel[sortedNodesAtLevel.length - 1].position + 1000;
    }

    addContent({
      variables: {
        input: {
          productUuid: product.uuid,
          position,
        },
      },
    });
  };

  handleOnDrop =
    ({ content, reorderNode }) =>
    (info) => {
      const contentByUuid = _keyBy(content, 'uuid');

      const dropKey = info.node.props.eventKey;
      // console.log('dropKey', dropKey);
      const dragKey = info.dragNode.props.eventKey;
      // console.log('dragKey', dragKey);
      const dropPos = info.node.props.pos.split('-');
      const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
      // console.log('dropPosition', dropPosition);

      const dropContent = contentByUuid[dropKey];
      const dragContent = _cloneDeep(contentByUuid[dragKey]);

      // 20190207JP: This isn't right - dropContent might be a sibling node.
      // if (!_isEmpty(dropContent.markdown) && (dropContent.markdown !== '(change me)')) {
      //   alert('Only nodes with no content are allowed to be parents'); // eslint-disable-line no-alert
      //   return;
      // }

      // if ((dropPosition === 0) && dropContent.parentUuid) {
      //   alert('You cannot nest content this deeply.'); // eslint-disable-line no-alert
      //   return;
      // }

      const nodesAtLevel = content.filter((c) => c.parentUuid === dropContent.parentUuid);
      const sortedNodesAtLevel = _sortBy(nodesAtLevel, 'position');
      // console.log('sortedNodesAtLevel', sortedNodesAtLevel);

      if (dropPosition === 0) {
        dragContent.parentUuid = dropContent.uuid;

        if (sortedNodesAtLevel.length === 0) {
          dragContent.position = 1000;
        } else {
          const lastNode = sortedNodesAtLevel[sortedNodesAtLevel.length - 1];
          dragContent.position = lastNode.position + 1000;
        }
      } else {
        dragContent.parentUuid = dropContent.parentUuid;

        const dropContentIndex = _findIndex(sortedNodesAtLevel, [
          'uuid',
          dropContent.uuid,
        ]);
        // console.log('dropContentIndex', dropContentIndex);

        if (dropPosition === -1) {
          if (dropContentIndex === 0) {
            dragContent.position = dropContent.position / 2.0;
          } else {
            dragContent.position =
              (dropContent.position + sortedNodesAtLevel[dropContentIndex - 1].position) /
              2.0;
          }
        } else {
          /* eslint-disable no-lonely-if */
          if (dropContentIndex === sortedNodesAtLevel.length - 1) {
            dragContent.position = dropContent.position + 1000;
          } else {
            dragContent.position =
              (dropContent.position + sortedNodesAtLevel[dropContentIndex + 1].position) /
              2.0;
          }
        }
      }

      reorderNode({
        uuid: dragContent.uuid,
        position: dragContent.position,
        parentUuid: dragContent.parentUuid,
      });
    };

  handleOnSelect = (e) => {
    const uuid = e.node.props.eventKey;
    const { history } = this.props;
    const { match } = this.props;
    const { params } = match;
    const { productSlug } = params;

    history.push(
      reverse(routes.staff.product.content.edit, { productSlug, contentUuid: uuid })
    );
  };

  renderTree = ({ product, content, reorderNode }) => {
    const { inflatedContent } = inflateContent({ product, content });

    const treeData = inflatedContent.map((contentLvl1) => ({
      key: contentLvl1.uuid,
      title: contentLvl1.title,
      children: contentLvl1.content.map((contentLvl2) => ({
        key: contentLvl2.uuid,
        title: contentLvl2.title,
        children: contentLvl2.content.map((contentLvl3) => ({
          key: contentLvl3.uuid,
          title: contentLvl3.title,
          children: contentLvl3.content.map((contentLvl4) => ({
            key: contentLvl4.uuid,
            title: contentLvl4.title,
            children: contentLvl4.content.map((contentLvl5) => ({
              key: contentLvl5.uuid,
              title: contentLvl5.title,
            })),
          })),
        })),
      })),
    }));

    return (
      <Tree
        draggable
        onDrop={this.handleOnDrop({ content, reorderNode })}
        onSelect={(_selectedKeys, e) => this.handleOnSelect(e)}
        treeData={treeData}
      />
    );
  };

  render() {
    const { match } = this.props;
    const { params } = match;
    const { productSlug } = params;

    return (
      <div className={styles.layout}>
        <Query query={STAFF_PRODUCT_CONTENT} variables={{ input: { slug: productSlug } }}>
          {({ loading, error, data }) => {
            if (loading || error) {
              return <SpinnerError error={error} />;
            }

            const {
              product: { product },
            } = data;
            const { content, slug } = product;

            return (
              <Mutation
                mutation={STAFF_ADD_CONTENT}
                refetchQueries={[
                  {
                    query: STAFF_PRODUCT_CONTENT,
                    variables: { input: { slug: productSlug } },
                  },
                ]}
              >
                {(addContent, { loading: addContentLoading, error: addContentError }) => {
                  if (addContentLoading || addContentError) {
                    return <SpinnerError error={addContentError} />;
                  }

                  return (
                    <Mutation
                      mutation={STAFF_DELETE_CONTENT}
                      refetchQueries={[
                        {
                          query: STAFF_PRODUCT_CONTENT,
                          variables: { input: { slug: productSlug } },
                        },
                      ]}
                      onCompleted={() => {
                        const { history } = this.props;
                        history.push(
                          reverse(routes.staff.product.content.list, { productSlug })
                        );
                      }}
                    >
                      {(deleteNode, { loading: loadingDelete, error: errorDelete }) => {
                        if (loadingDelete || errorDelete) {
                          return <SpinnerError error={errorDelete} />;
                        }

                        return (
                          <Mutation mutation={STAFF_REORDER_CONTENT}>
                            {(
                              reorderNode,
                              { loading: loadingReorder, error: errorReorder }
                            ) => {
                              if (loadingReorder || errorReorder) {
                                return <SpinnerError error={errorReorder} />;
                              }

                              const handleReorder = ({ uuid, parentUuid, position }) => {
                                reorderNode({
                                  variables: {
                                    input: {
                                      uuid,
                                      parentUuid,
                                      position,
                                    },
                                  },
                                });
                              };

                              return (
                                <div className={styles.columns}>
                                  <div className={styles.sidebar}>
                                    <b>{slug}</b>
                                    {this.renderTree({
                                      product,
                                      content,
                                      reorderNode: handleReorder,
                                    })}
                                    <br />
                                    <Button
                                      onClick={() =>
                                        this.handleAddNode({ addContent, product })
                                      }
                                    >
                                      Add node
                                    </Button>
                                  </div>
                                  <div className={styles.content}>
                                    <PropsRoute
                                      exact
                                      path={routes.staff.product.content.edit}
                                      component={ProductContentEdit}
                                      content={content}
                                      deleteNode={deleteNode}
                                      reorderNode={reorderNode}
                                      product={product}
                                    />
                                  </div>
                                </div>
                              );
                            }}
                          </Mutation>
                        );
                      }}
                    </Mutation>
                  );
                }}
              </Mutation>
            );
          }}
        </Query>
      </div>
    );
  }
}

export default ProductContent;
